'use client';

import { useEffect, useRef } from 'react';
import { sendGTMEvent } from '../sendGTMEvent';
import { useRouter } from 'next/router';
import type { CatalogueModel } from 'api-types';
import { useGTMQueue } from '../useGTMQueue';
import { getSelectItemData } from '../itemSelectionUtils';
import { productToGAItem } from '../productMappers/productToGAItem';

type Props = {
  product?: CatalogueModel.ProductVariant;
};

/**
 * Handles tracking of the view_item event.
 *
 * Expected to only be rendered with the Product page.
 */

export const ViewItemTracker = ({ product }: Props) => {
  const router = useRouter();
  const pathname = router.asPath;
  const prevPathnameRef = useRef<string | null>(null);
  const { addToQueue } = useGTMQueue();

  useEffect(() => {
    const hasTrackedOnCurrentPath = prevPathnameRef.current === pathname;
    const shouldNotTrack = !product || hasTrackedOnCurrentPath;

    if (shouldNotTrack) {
      return;
    }

    const productKey = product.key || product.id?.toString();
    const productId = product.id?.toString();
    const selectItemData = productKey && productId ? getSelectItemData(productKey, productId) : undefined;

    addToQueue(() => {
      sendGTMEvent({
        event: 'view_item',
        ecommerce: {
          items: [
            productToGAItem(product, {
              listId: selectItemData?.item_list_id,
              listName: selectItemData?.item_list_name,
              index: selectItemData?.index,
            }),
          ],
        },
      });
    });

    prevPathnameRef.current = pathname;
  }, [pathname, product]);

  return null;
};
